import { getConfidenceClass } from "../../Document/getConfidenceClass";
import { GetHighlightedText } from "../../Document/getHighlightedText";
import { State, TaxonomyEntryType } from "../../types";
import { extractKeysAndIndexes } from "../getDefaultState";
import { mapTextToValue } from "../util";

export type SelectBlockAction = Readonly<{
  blockText: string;
  idRef: string;
  replaceValue: boolean;
  type: "SELECT BLOCK";
}>;

const CONFIRMATION_MESSAGE = "Are you sure you want to update the supporting text for this answer?";

export const selectBlock = (state: State, action: SelectBlockAction): State => {
  if (!(state.activeValue in state.values)) {
    return state;
  }
  const value = state.values[state.activeValue];
  if (value.idRef === action.idRef && !action.replaceValue) {
    return state;
  }
  if (
    value.idRef !== null &&
    value.idRef !== action.idRef &&
    !window.confirm(CONFIRMATION_MESSAGE)
  ) {
    return state;
  }
  const previousIdRef = value.idRef;
  let updatedValue = { ...value, humanEdited: true, idRef: action.idRef };

  const taxonomyEntry = extractKeysAndIndexes(updatedValue.valueKey, state.taxonomy.taxonomyObject).taxonomyEntry;
  if (taxonomyEntry.type === TaxonomyEntryType.Group) {
    // we cannot update groups
    return state;
  }
  const updatedBlocks: State["blocks"] = {};
  if (action.replaceValue || taxonomyEntry.type === TaxonomyEntryType.Pointer) {
    try {
      const newValues = mapTextToValue(action.blockText, taxonomyEntry);
      updatedValue = {
        ...updatedValue,
        value: newValues[0],
        valueRaw: newValues[1],
      };
      updatedBlocks[action.idRef] = {
        ...state.blocks[action.idRef]
      };
    } catch (err) {
      // unable to update the value for replacement
      console.error("Unable to update value for replacement.");
      console.error(err);
    }
  }
  const values = {
    ...state.values,
    [updatedValue.valueKey]: updatedValue,
  };
  if (previousIdRef !== updatedValue.idRef) {
    if (
      previousIdRef &&
      previousIdRef in state.blocks &&
      state.blocks[previousIdRef].references.includes(value.valueKey)
    ) {
      updatedBlocks[previousIdRef] = {
        ...state.blocks[previousIdRef],
        references: state.blocks[previousIdRef].references.filter(r => r !== value.valueKey),
      };
    }
    if (
      action.idRef &&
      action.idRef in state.blocks &&
      !state.blocks[action.idRef].references.includes(value.valueKey)
    ) {
      updatedBlocks[action.idRef] = {
        ...state.blocks[action.idRef],
        references: state.blocks[action.idRef].references.concat([value.valueKey]),
      };
    }
  }
  let blocks = state.blocks;
  if (Object.keys(updatedBlocks).length > 0) {
    // merge the new updated blocks into the state and recalculate their confidence classes
    blocks = {
      ...blocks,
      ...Object.keys(updatedBlocks).reduce((prev, next) => {
        prev[next] = {
          ...prev[next],
          confidenceClass: getConfidenceClass(prev[next], state.confidenceThreshold, values),
          highlights: GetHighlightedText(prev[next], state.taxonomy.taxonomyObject, values),
        };
        return prev;
      }, updatedBlocks),
    };
  }
  return {
    ...state,
    activeValue: updatedValue.valueKey,
    blocks,
    modified: true,
    values,
  };
};
